import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import PropTypes from "prop-types";
import { Autocomplete } from "@mui/material";
import { parse } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";
import axios from "axios";
import {
	icon,
	IconComponent,
	CustomButton,
	SidePanel,
	SidePanelContent,
	CustomDatePicker,
	CustomTextField,
} from "../../../../common/components";
import { translate } from "../../../../common/providers";
import { createNotification } from "../../../../common/components/notifications/NotificationComponents";
import { CommitmentService } from "../../../../api";
import ConfirmationDialog from "./ConfirmationDialog";
import { capitalizeAllWords, convertAnyDateToDisplayDate, isNonEmptyArray } from "../../../../common/utils";
import { translateFilterOptionLabel } from "../../utils/translate-utils";
import { useApi } from "../../../../common/hooks";
import { setManualSelectionThemeUuuid, setSelectedCommitment } from "../../slice/commitmentTrackingSlice";
import styles from "./CommitmentSidePanel.module.css";

const reorderPriorities = (data) => {
	const order = ["WEAK", "NORMAL", "VERY_URGENT"];
	return [...data].sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
};

const reorderRisk = (data) => {
	const order = ["LOW", "NORMAL", "HIGH", "VERY_HIGH"];
	return [...data].sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
};
export default function CommitmentSidePanel({
	open,
	onClose,
	onSubmit,
	projectId,
	filterDefinitions = [],
	commitment = null,
	mode = "create",
}) {
	const dispatch = useDispatch();
	const language = useSelector(({ context }) => context.language.value);
	const localeLanguage = useMemo(
		() => (language === "en" && enUS) || (language === "fr" && fr) || (language === "esp" && es) || enUS,
		[language]
	);
	const isEditMode = mode === "edit" && commitment;
	const fieldOptions = useMemo(
		() =>
			(
				(isNonEmptyArray(filterDefinitions) &&
					filterDefinitions.filter((filter) =>
						["theme", "frequency", "priority", "risk"].includes(filter.fieldName)
					)) ||
				[]
			).reduce((acc, obj) => {
				acc[obj.fieldName] = (obj.possibleValues || [])
					.filter((item) => !item.isDeprecated)
					.map((item) => ({
						label: item.value || item.label || item,
						value: item.value || item,
						valueUuid: item.valueUuid,
					}));
				return acc;
			}, {}) || { theme: [], frequency: [], priority: [], risk: [], pilot: [] },
		[filterDefinitions]
	);
	const [formData, setFormData] = useState({
		theme: "",
		name: "",
		details: "",
		pilot: "",
		frequency: "",
		risk: "",
		priority: "",
		completionDeadline: null,
	});
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pilotInput, setPilotInput] = useState("");
	const [selectedPilot, setSelectedPilot] = useState(null);
	const [pilotOptions, setPilotOptions] = useState([]);
	const { call: updateCommitment } = useApi(CommitmentService.updateCommitment);
	const { call: createCommitment } = useApi(CommitmentService.createCommitment);
	const { call: getPilots } = useApi(CommitmentService.getPilots);
	const transformCommitmentToFormData = useCallback((commitmentData) => {
		if (!commitmentData) {
			return null;
		}
		const formValues = {
			name: "",
			theme: "",
			details: "",
			pilot: "",
			frequency: "",
			risk: "",
			priority: "",
			completionDeadline: null,
		};
		if (commitmentData.details) {
			formValues.details = commitmentData.details;
		} else if (commitmentData.fieldsValues) {
			const detailsField = commitmentData.fieldsValues.find(
				(f) => f.fieldName?.toLowerCase() === "details" || f.fieldName?.toLowerCase() === "description"
			);
			if (detailsField?.values?.[0]) {
				formValues.details = detailsField.values[0].value || detailsField.values[0];
			}
		}
		if (commitmentData.name) {
			formValues.name = commitmentData.name;
		}
		const extractValue = (fieldNames) => {
			for (const fieldName of fieldNames) {
				const directValue = commitmentData[fieldName];
				if (directValue !== undefined && directValue !== null) {
					if (Array.isArray(directValue)) {
						return directValue.length > 0 ? directValue[0] : null;
					}
					return directValue;
				}
			}
			if (commitmentData.fieldsValues && Array.isArray(commitmentData.fieldsValues)) {
				for (const fieldName of fieldNames) {
					const field = commitmentData.fieldsValues.find(
						(f) => f.fieldName?.toLowerCase() === fieldName.toLowerCase()
					);
					if (field?.values && field.values.length > 0) {
						const value = field.values[0];
						return typeof value === "object" ? value.value : value;
					}
				}
			}
			return null;
		};
		const themeValue = extractValue(["theme"]);
		if (themeValue) {
			formValues.theme = themeValue;
		}
		const frequencyValue = extractValue(["frequency"]);
		if (frequencyValue) {
			formValues.frequency = frequencyValue;
		}
		const priorityValue = extractValue(["priority"]);
		if (priorityValue) {
			formValues.priority = priorityValue;
		}
		const riskValue = extractValue(["risk"]);
		if (riskValue) {
			formValues.risk = riskValue;
		}
		const pilotValue = extractValue(["pilot"]);
		if (pilotValue) {
			formValues.pilot = String(pilotValue);
			setSelectedPilot(formValues.pilot);
		}
		const deadlineValue = extractValue(["completionDeadline"]);
		if (deadlineValue) {
			try {
				let date = null;
				if (typeof deadlineValue === "string") {
					if (deadlineValue.includes("/")) {
						date = parse(deadlineValue, "dd/MM/yyyy", new Date());
					} else {
						date = new Date(deadlineValue);
					}
				} else if (deadlineValue instanceof Date) {
					date = deadlineValue;
				}

				if (date && !Number.isNaN(date.getTime())) {
					formValues.completionDeadline = date;
				}
			} catch (error) {
				console.error("Error parsing deadline date:", error);
			}
		}
		return formValues;
	}, []);
	useEffect(() => {
		if (open) {
			if (isEditMode) {
				const initialData = transformCommitmentToFormData(commitment);
				if (initialData) {
					setPilotInput("");
					setFormData(initialData);
				}
			} else {
				setPilotInput("");
				setFormData({
					name: "",
					theme: "",
					details: "",
					pilot: "",
					frequency: "",
					risk: "",
					priority: "",
					completionDeadline: null,
				});
			}
			setErrors({});
		}
	}, [open, isEditMode, commitment, transformCommitmentToFormData]);
	const getFieldOptionsForAutocomplete = useCallback(
		(fieldName, currentValue) => {
			if (fieldOptions[fieldName] && fieldOptions[fieldName].length > 0) {
				return fieldOptions[fieldName];
			}
			if (currentValue) {
				return [{ label: currentValue, value: currentValue }];
			}
			return [];
		},
		[fieldOptions]
	);
	const handleFetchPilots = useCallback(async () => {
		try {
			const response = await getPilots({ projectId }, { name: "" });
			setPilotOptions(response.map((pilot) => ({ label: pilot, value: pilot, valueUuid: pilot })));
		} catch (error) {
			console.error("Error fetching pilot options:", error);
		}
	}, [getPilots, projectId]);
	const handleClose = () => {
		setFormData({
			name: "",
			theme: "",
			details: "",
			pilot: "",
			frequency: "",
			risk: "",
			priority: "",
			completionDeadline: null,
		});
		setErrors({});
		onClose();
	};
	const handleChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
		if (field === "pilot" && !value) {
			setFormData((prev) => ({ ...prev, [field]: pilotInput }));
		}
	};
	const validateForm = () => {
		const newErrors = {};
		if (
			!formData.theme ||
			!filterDefinitions
				.find((definition) => definition.fieldName === "theme")
				?.possibleValues?.some((value) => value.value === formData.theme)
		) {
			newErrors.theme = translate("commitment-tracking.engagement-dialog.theme-error");
		}
		if (!formData.details) {
			newErrors.details = translate("commitment-tracking.engagement-dialog.details-error");
		}
		if (!formData.name) {
			newErrors.name = translate("commitment-tracking.engagement-dialog.name-error");
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};
	const performSubmit = async () => {
		let formattedDeadline;
		if (formData.completionDeadline) {
			formattedDeadline = convertAnyDateToDisplayDate(formData.completionDeadline);
		} else {
			formattedDeadline = null;
		}
		const themeUuid = formData.theme
			? fieldOptions.theme.find((opt) => opt.value === formData.theme)?.valueUuid
			: null;
		const frequencyUuid = formData.frequency
			? fieldOptions.frequency.find((opt) => opt.value === formData.frequency)?.valueUuid
			: null;
		const riskUuid = formData.risk ? fieldOptions.risk.find((opt) => opt.value === formData.risk)?.valueUuid : null;
		const priorityUuid = formData.priority
			? fieldOptions.priority.find((opt) => opt.value === formData.priority)?.valueUuid
			: null;
		const payload = {
			theme: themeUuid || null,
			name: formData.name,
			details: formData.details,
			pilot: capitalizeAllWords(formData.pilot || pilotInput || ""),
			frequency: frequencyUuid || null,
			risk: riskUuid || null,
			priority: priorityUuid || null,
			completionDeadline: formattedDeadline,
		};
		setLoading(true);
		try {
			let response;
			if (isEditMode) {
				response = await updateCommitment({ commitmentId: commitment.commitmentId }, payload);
			} else {
				response = await createCommitment({ projectId: projectId || 1 }, payload);
			}
			if (onSubmit) {
				await onSubmit(response, payload);
			}
			createNotification({
				type: "success",
				message: isEditMode
					? translate("commitment-tracking.engagement-dialog.success-update")
					: translate("commitment-tracking.engagement-dialog.success-create"),
			});
			if (isEditMode) {
				dispatch(
					setSelectedCommitment({
						...commitment,
						...formData,
						pilot: payload.pilot,
					})
				);
			}
			if (!isEditMode) {
				dispatch(setManualSelectionThemeUuuid(themeUuid));
			}
			handleClose();
		} catch (error) {
			if (axios.isCancel(error)) {
				console.error("Request canceled:", error.message);
			} else {
				console.error(`Error ${isEditMode ? "updating" : "creating"} commitment:`, error);
				console.error("Error details:", error.response?.data);
			}
		} finally {
			setLoading(false);
		}
	};
	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}
		if (isEditMode) {
			setShowConfirmDialog(true);
			return;
		}
		await performSubmit();
	};
	const handleConfirmSubmit = async () => {
		setShowConfirmDialog(false);
		await performSubmit();
	};
	const handleCancelConfirm = () => {
		setShowConfirmDialog(false);
	};
	const dialogConfig = {
		title: isEditMode
			? translate("commitment-tracking.engagement-dialog.edit-title")
			: translate("commitment-tracking.engagement-dialog.create-title"),
		submitLabel: (() => {
			if (loading) {
				return isEditMode
					? translate("commitment-tracking.engagement-dialog.updating")
					: translate("commitment-tracking.engagement-dialog.creating");
			}
			return translate("commitment-tracking.engagement-dialog.submit");
		})(),
	};
	const isFormValid = useMemo(
		() =>
			formData.theme &&
			filterDefinitions
				.find((definition) => definition.fieldName === "theme")
				?.possibleValues?.some((value) => value.value === formData.theme) &&
			formData.details?.length > 0 &&
			formData.name?.length > 0,
		[formData, filterDefinitions]
	);
	const handleChangPilot = (newValue) => {
		if (!newValue) {
			return;
		}
		if (typeof newValue === "string") {
			setPilotInput(newValue);
			setSelectedPilot(null);
			setFormData((prev) => ({ ...prev, pilot: newValue }));
		} else {
			setPilotInput(newValue.label);
			setSelectedPilot(newValue);
			setFormData((prev) => ({ ...prev, pilot: newValue.value }));
		}
	};
	const handleInputPilotChange = (newInputValue) => {
		const nameRegex = /^[a-zA-Zàâäéèêëîïôöùûüçÿœæ\s\-'.]+$/;
		if (newInputValue === "" || nameRegex.test(newInputValue)) {
			setPilotInput(newInputValue);
			const exactMatch = fieldOptions?.pilot?.find(
				(opt) => opt.label.toLowerCase() === newInputValue.toLowerCase()
			);
			setSelectedPilot(exactMatch || null);
			setFormData((prev) => ({
				...prev,
				pilot: exactMatch ? exactMatch.value : newInputValue,
			}));
		}
	};
	return (
		<>
			<SidePanel disableBackdropClick open={open} position="right" size={50} onClose={handleClose}>
				<SidePanelContent
					className={styles.flexDirectionColumn}
					title={dialogConfig.title}
					onClose={handleClose}
				>
					<div className={styles.dialogBody}>
						<div className={styles.formContainer}>
							<div className={styles.fieldWrapper}>
								<div className={styles.fieldLabel}>
									{translate("commitment-tracking.engagement-dialog.theme")}
									<span className={styles.required}>
										{translate("commitment-tracking.engagement-dialog.theme-required")}
									</span>
								</div>
								<Autocomplete
									fullWidth
									disabled={commitment?.nameIsGeneratedByAI}
									getOptionLabel={(option) => option.label}
									isOptionEqualToValue={(option, value) => option.value === value?.value}
									noOptionsText={translate("commitment-tracking.engagement-dialog.theme-no-options")}
									options={getFieldOptionsForAutocomplete("theme", formData.theme)}
									renderInput={(params) => (
										<CustomTextField
											{...params}
											error={!!errors.theme}
											helperText={errors.theme}
											placeholder={
												formData.theme ||
												translate("commitment-tracking.engagement-dialog.theme-placeholder")
											}
											variant="outlined"
										/>
									)}
									size="small"
									value={fieldOptions?.theme?.find((opt) => opt.value === formData.theme) || null}
									onChange={(e, newValue) => handleChange("theme", newValue?.value || "")}
								/>
							</div>
							<div className={styles.fieldWrapper}>
								<div className={styles.fieldLabel}>
									{translate("commitment-tracking.engagement-dialog.name")}
									<span className={styles.required}>
										{translate("commitment-tracking.engagement-dialog.details-required")}
									</span>
								</div>
								<CustomTextField
									fullWidth
									disabled={commitment?.nameIsGeneratedByAI}
									error={!!errors.name}
									helperText={errors.name}
									placeholder={translate("commitment-tracking.engagement-dialog.name.placeholder")}
									size="small"
									value={formData.name}
									variant="outlined"
									onChange={(e) => handleChange("name", e.target.value)}
								/>
							</div>
							<div className={styles.fieldWrapper}>
								<div className={styles.fieldLabel}>
									{translate("commitment-tracking.engagement-dialog.details")}
									<span className={styles.required}>
										{translate("commitment-tracking.engagement-dialog.details-required")}
									</span>
								</div>
								<CustomTextField
									fullWidth
									multiline
									error={!!errors.details}
									helperText={errors.details}
									maxRows={15}
									minRows={5}
									placeholder={translate("commitment-tracking.engagement-dialog.details-placeholder")}
									size="small"
									value={formData.details}
									variant="outlined"
									onChange={(e) => handleChange("details", e.target.value)}
								/>
							</div>
							<div className={styles.formRow}>
								<div className={styles.formField}>
									<div className={styles.fieldLabel}>
										{translate("commitment-tracking.engagement-dialog.pilot")}
									</div>
									<Autocomplete
										freeSolo
										fullWidth
										openOnFocus
										filterOptions={(options, { inputValue }) =>
											options.filter((option) =>
												option.label.toLowerCase().startsWith(inputValue.toLowerCase())
											)
										}
										getOptionLabel={(option) =>
											typeof option === "string" ? option : option.label
										}
										inputValue={pilotInput}
										isOptionEqualToValue={(option, value) => option.value === value.value}
										options={pilotOptions.filter((p) => p.valueUuid) || []}
										renderInput={(params) => (
											<CustomTextField
												{...params}
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<>
															{params.InputProps.endAdornment}
															<IconComponent
																className={styles.fieldIcon}
																icon={icon.faUser}
															/>
														</>
													),
												}}
												placeholder={
													formData.pilot ||
													translate("commitment-tracking.engagement-dialog.pilot-placeholder")
												}
												variant="outlined"
											/>
										)}
										value={selectedPilot}
										onChange={(_e, newValue) => handleChangPilot(newValue)}
										onInputChange={(_e, newInputValue) => handleInputPilotChange(newInputValue)}
										onOpen={handleFetchPilots}
									/>
								</div>
								<div className={styles.formField}>
									<div className={styles.fieldLabel}>
										{translate("commitment-tracking.engagement-dialog.deadline")}
									</div>
									<LocalizationProvider adapterLocale={localeLanguage} dateAdapter={AdapterDateFns}>
										<CustomDatePicker
											slotProps={{
												textField: {
													placeholder: translate(
														"commitment-tracking.engagement-dialog.deadline-placeholder"
													),
													fullWidth: true,
													size: "small",
													variant: "outlined",
													className: styles.input,
												},
											}}
											value={formData.completionDeadline}
											onChange={(newValue) => handleChange("completionDeadline", newValue)}
										/>
									</LocalizationProvider>
								</div>
								<div className={styles.formField}>
									<div className={styles.fieldLabel}>
										{translate("commitment-tracking.engagement-dialog.frequency")}
									</div>
									<Autocomplete
										fullWidth
										getOptionLabel={(option) =>
											translateFilterOptionLabel("frequency", option.label)
										}
										isOptionEqualToValue={(option, value) => option.value === value?.value}
										noOptionsText={translate(
											"commitment-tracking.engagement-dialog.frequency-no-options"
										)}
										options={getFieldOptionsForAutocomplete("frequency", formData.frequency)}
										renderInput={(params) => (
											<CustomTextField
												{...params}
												placeholder={
													formData.frequency ||
													translate(
														"commitment-tracking.engagement-dialog.frequency-placeholder"
													)
												}
												variant="outlined"
											/>
										)}
										size="small"
										value={
											fieldOptions?.frequency?.find((opt) => opt.value === formData.frequency) ||
											null
										}
										onChange={(e, newValue) => handleChange("frequency", newValue?.value || "")}
									/>
								</div>
							</div>
							<div className={styles.formRow}>
								<div className={styles.formField}>
									<div className={styles.fieldLabel}>
										{translate("commitment-tracking.engagement-dialog.priority")}
									</div>
									<Autocomplete
										fullWidth
										getOptionLabel={(option) =>
											translateFilterOptionLabel("priority", option.label)
										}
										isOptionEqualToValue={(option, value) => option.value === value?.value}
										noOptionsText={translate(
											"commitment-tracking.engagement-dialog.priority-no-options"
										)}
										options={reorderPriorities(
											getFieldOptionsForAutocomplete("priority", formData.priority)
										)}
										renderInput={(params) => (
											<CustomTextField
												{...params}
												placeholder={
													formData.priority ||
													translate(
														"commitment-tracking.engagement-dialog.priority-placeholder"
													)
												}
												variant="outlined"
											/>
										)}
										size="small"
										value={
											fieldOptions?.priority?.find((opt) => opt.value === formData.priority) ||
											null
										}
										onChange={(e, newValue) => handleChange("priority", newValue?.value || "")}
									/>
								</div>
								<div className={styles.formField}>
									<div className={styles.fieldLabel}>
										{translate("commitment-tracking.engagement-dialog.risk-label")}
									</div>
									<Autocomplete
										fullWidth
										getOptionLabel={(option) => translateFilterOptionLabel("risk", option.label)}
										isOptionEqualToValue={(option, value) => option.value === value?.value}
										noOptionsText={translate(
											"commitment-tracking.engagement-dialog.risk-no-options"
										)}
										options={reorderRisk(getFieldOptionsForAutocomplete("risk", formData.risk))}
										renderInput={(params) => (
											<CustomTextField
												{...params}
												placeholder={
													formData.risk ||
													translate("commitment-tracking.engagement-dialog.risk-placeholder")
												}
												variant="outlined"
											/>
										)}
										size="small"
										value={fieldOptions?.risk?.find((opt) => opt.value === formData.risk) || null}
										onChange={(e, newValue) => handleChange("risk", newValue?.value || "")}
									/>
								</div>
							</div>
						</div>
					</div>
					<div className={styles.dialogFooter}>
						<CustomButton color="secondary" disabled={loading} variant="outlined" onClick={handleClose}>
							{translate("common:btn.cancel")}
						</CustomButton>
						<CustomButton
							color="primary"
							disabled={loading || !isFormValid}
							variant="contained"
							onClick={handleSubmit}
						>
							{dialogConfig.submitLabel}
						</CustomButton>
					</div>
				</SidePanelContent>
			</SidePanel>
			<ConfirmationDialog
				cancelLabel={translate("common:btn.cancel")}
				confirmLabel={translate("commitment-tracking.engagement-dialog.confirm-submit")}
				message={translate("commitment-tracking.engagement-dialog.confirm-message")}
				open={showConfirmDialog}
				title={translate("commitment-tracking.engagement-dialog.confirm-title")}
				onClose={handleCancelConfirm}
				onConfirm={handleConfirmSubmit}
			/>
		</>
	);
}

CommitmentSidePanel.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func,
	projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	filterDefinitions: PropTypes.arrayOf(PropTypes.shape({})),
	commitment: PropTypes.shape({}),
	mode: PropTypes.oneOf(["create", "edit"]),
};

CommitmentSidePanel.defaultProps = {
	onSubmit: null,
	projectId: null,
	filterDefinitions: [],
	commitment: null,
	mode: "create",
};
